import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, notification, Modal } from 'antd';
import {
  Edit,
  EditProps,
  SaveButton,
  SimpleForm,
  useNotify, useRedirect,
} from 'react-admin';
import { Toolbar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TextArea from 'antd/es/input/TextArea';

import { checkSuperadmin } from '@providers';
import { Forbidden, InitialPage, Loader, ReminderComponent } from '@components';
import { AnswerCol, AnswerRow, Credit, Doctype, SurveyQuestion } from '@types';
import { useEditProps, useQuery, useRbacEdit, useRbacView, useRecord } from '@hooks';
import { axiosErrorToString } from '@utils';
import {
  creditHttp,
  CreditSurveyTableColProps,
  CreditSurveyTableRowProps,
  UpdateCreditSurveyType,
  surveyHttp,
} from '@network';

import { CreditCallsPage } from './CreditCallsPage';
import { CreditFarmsPage } from './CreditFarmsPage';
import { CreditInitial } from '@pages/credit/CreditInitial';
import { CreditDocuments } from '@pages/credit/CreditWidget';
import { CreditReportPage } from '@pages/credit/CreditReport';
import { CreditUser } from '@pages/credit/CreditEdit/CreditUser';
import { CreditInfo } from '@pages/credit/CreditEdit/CreditInfo';
import { CreditHistory } from '@pages/credit/CreditEdit/CreditHistory';
import { CreditAccredited } from '@pages/credit/CreditEdit/CreditAccredited';
import { CreditGuarantor, CreditGuarantorCreate } from '@pages/credit/CreditEdit/CreditGuarantor';
import { CreditSurveyDetail } from '@pages/credit/CreditEdit/CreditSurveyDetail';
import { CreditDocumentsLink } from '@pages/credit/CreditEdit/CreditDocumentsLink';
import { CreditContractsDetails } from '@pages/credit/CreditInitial/CreditContractsDetails';
import { CreditInvoicesDetail, CreditInvoicesPage } from './CreditInvoices';
import { CreditSurveyProgress } from '@pages/credit/CreditInitial/CreditSurveyProgress';
import { CreditFarms } from '@pages/credit/CreditEdit/CreditFarms';
import { CreditIncomeVerification } from '@pages/credit/CreditEdit/CreditIncomeVerification';
import { CreditSurveyView } from '@pages/credit/CreditEdit/CreditSurveyView';
import { CreditActions } from '@pages/credit/CreditEdit/CreditActions';
import { CreditDates } from './CreditDates';
import { CreditCrops } from './CreditCrops';
import { CreditGuarantors } from '@pages/credit/CreditInitial/CreditGuarantors';
import { CreditFileScreeningWidget } from '@pages/credit/CreditEdit/CreditFileScreening/CreditFileScreeningWidget';
import { CreditFileStatusWidget } from '@pages/credit/CreditEdit/CreditFileScreening/CreditFileStatusWidget';
import { CreditComments } from '@pages/credit/CreditEdit/CreditComments';

const Title = ({ record }: {record?: any}) => {
  return record ? (<span>Credit #{record.id}</span>) : null;
};

const DeleteWithReasonButton = ({ credit }: { credit: Credit }) => {
  const cls = useStyles();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [removalReason, setRemovalReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setRemovalReason('');
  };

  const handleDelete = async () => {
    if (!removalReason.trim()) {
      notify('Removal reason is empty', { type: 'warning' });
      return;
    }

    setIsDeleting(true);
    try {
      await creditHttp.delete(`/${credit.id}`, { removalReason });
      notify('Credit has been deleted');
      setIsModalVisible(false);
      redirect('/credit');
    } catch (error) {
      console.error(error);
      const errorMessage = axiosErrorToString(error);
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={showModal}
        className={cls.removeBtn}
      >
          Delete
      </button>

      <Modal
        title="Are you sure?"
        open={isModalVisible}
        onOk={handleDelete}
        onCancel={handleCancel}
        okText="Delete"
        cancelText="Cancel"
        confirmLoading={isDeleting}
        okButtonProps={{
          danger: true,
          disabled: !removalReason.trim(),
        }}
      >
        <div>
          <label htmlFor="removalReason"></label>
          <TextArea
            id="removalReason"
            value={removalReason}
            onChange={(e) => setRemovalReason(e.target.value)}
            placeholder=""
            rows={4}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </>
  );
};

export const CreditEdit = (props: EditProps) => {
  const cls = useStyles();
  const query = useQuery();
  const initialCredit = useRecord<Credit>(props);
  const editProps = useEditProps();
  const scrollRef = useRef<any>(null);
  const [credit, setCredit] = useState<Credit>();
  const [requiredDoctypeIds, setRequiredDoctypeIds] = useState<number[]>([]);
  const [guarantorDoctypes, setGuarantorDoctypes] = useState<Doctype[]>([]);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [savedQuestions, setSavedQuestions] = useState<SurveyQuestion[]>([]);
  const [isFixedSurvey, setIsFixedSurvey] = useState(false);
  const [answeredDate, setAnsweredDate] = useState<Date | null>(null);

  const reloadCredit = useCallback(async () => {
    if (!initialCredit) return;

    const nextCredit = await creditHttp.getCredit(initialCredit.id);
    setCredit(nextCredit);
  }, [initialCredit]);

  const isSuperadmin = checkSuperadmin();
  const rbacView = useRbacView();
  const editable = useRbacEdit() && !credit?.createdByDealerId;

  const getSurvey = async () => {
    if (credit) {
      const {
        surveyQuestions, answeredDate: loadedAnsweredDate, isFixed,
      } = await surveyHttp.getCreditSurvey(credit.id);

      setIsFixedSurvey(isFixed);
      setQuestions(surveyQuestions.map((q, index) => {
        q.position = index + 1;
        return q;
      }));
      setSavedQuestions(surveyQuestions.map((q, index) => {
        q.position = index + 1;
        return q;
      }));
      setAnsweredDate(loadedAnsweredDate);
    }
  };

  const onScrollToDocs = (el: HTMLElement) => {
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 1000);
  };

  const onSaveQuestions = async (questions: SurveyQuestion[]) => {
    if (isFixedSurvey) {
      return;
    }
    if (credit?.id && questions.length) {
      await Promise.all(
        questions.map(q => {
          let type: UpdateCreditSurveyType;

          if (q.isPlanner) {
            type = 'answer-percent';
          } else if (q.isTable) {
            type = 'answer-table';
          } else {
            type = 'answer';
          }

          const params = q.isTable ? {
            rows: q.rows.map((r: AnswerRow): CreditSurveyTableRowProps => ({
              cols: r.cols.map((c: AnswerCol): CreditSurveyTableColProps => ({
                answerId: c.answerId,
                customAnswer: c.customAnswer,
              })),
            })),
          } : q.isPlanner ? {
            rows: q.rows.map((r: AnswerRow): CreditSurveyTableRowProps => ({
              cols: r.cols.map((c: AnswerCol): CreditSurveyTableColProps => ({
                answerId: c.answerId,
                customAnswer: c.customAnswer,
              })),
            })),
          } : { answerIds: q.answerIds, customAnswer: q.customAnswer || undefined };

          return surveyHttp.updateCreditSurvey({
            creditId: credit.id,
            questionId: q.id,
            params,
            type,
          });
        }),
      ).then(() => {
        notification.success({ message: 'Survey is saved', duration: 8 });
        getSurvey();
      }).catch((e) => {
        console.error(e);
        const errorMessage = axiosErrorToString(e);
        notification.error({ message: errorMessage, duration: 8 });
      });
    }
  };

  useEffect(() => {
    initialCredit && (async () => {
      const res = await creditHttp.getCreditDoctypes(initialCredit.id);
      const requiredDoctypeIdsList: number[] = [];
      (res.doctypes || []).forEach(d => {
        if (d.isSendToCredit) {
          requiredDoctypeIdsList.push(d.id);
        }
      });
      setRequiredDoctypeIds(requiredDoctypeIdsList);
      setGuarantorDoctypes(res.guarantorDoctypes);
    })();
    reloadCredit();
  }, [initialCredit, reloadCredit]);

  useEffect(() => {
    getSurvey();
  }, [credit?.id]);

  const showInitial = query.get('initial') === '';
  const showSurvey = query.get('survey') === '';
  const showSurveyView = query.get('survey-view') === '';
  const showFarms = query.get('farms') === '';
  const showCalls = query.get('calls') === '';
  const showReport = query.get('report') === '';
  const showContracts = query.get('contracts') === '';
  const scrollToDocs = query.get('scroll') === 'docs';

  const createGuarantor = query.get('guarantor-create') === '';
  const guarantorId = query.get('guarantor');
  const showGuarantor = !!guarantorId;
  const showInvoices = query.get('invoices') === '';
  const invoiceDetail = query.get('invoices');
  const showInvoiceDetail = !!invoiceDetail;

  const transform = (data: any) => {
    const { status, bankId, docsLink } = data;

    return { status, bankId, docsLink } as any;
  };

  useEffect(() => {
    if (scrollToDocs && scrollRef.current) {
      const element = document.getElementById('docs');
      element && onScrollToDocs(element);
    }
  }, [scrollRef.current]);

  if (!rbacView) {
    return <Forbidden />;
  }

  if (!credit) {
    return <Loader />;
  }

  if (showInitial) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditInitial credit={credit} editable={editable} />
      </Edit>
    ) : <Loader />;
  }

  if (showSurvey) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditSurveyDetail
          credit={credit}
          editable={editable}
          isFixedSurvey={isFixedSurvey}
          questions={questions}
          savedQuestions={savedQuestions}
          setQuestions={setQuestions}
          answeredDate={answeredDate}
          onUpdate={onSaveQuestions}
        />
      </Edit>
    ) : <Loader />;
  }

  if (showSurveyView) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditSurveyView credit={credit}/>
      </Edit>
    ) : <Loader />;
  }

  if (showFarms) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditFarmsPage
          credit={credit}
          setCredit={setCredit}
          editable={editable} />
      </Edit>
    ) : <Loader />;
  }

  if (showCalls) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <SimpleForm toolbar={<></>}>
          <CreditCallsPage credit={credit} editable={editable} />
        </SimpleForm>
      </Edit>
    ) : <Loader />;
  }

  if (showReport) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditReportPage
          credit={credit}
          setCredit={setCredit}
          requiredDoctypeIds={requiredDoctypeIds}
          questions={questions.filter(q => q.isSendToCredit) || []}
          guarantorDoctypes={guarantorDoctypes}
        />
      </Edit>
    ) : <Loader />;
  }

  if (showInvoices) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditInvoicesPage credit={credit} setCredit={setCredit} editable={editable} />
      </Edit>
    ) : <Loader />;
  }

  if (showInvoiceDetail) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditInvoicesDetail credit={credit} invoiceId={+invoiceDetail} />
      </Edit>
    ) : <Loader />;
  }

  if (createGuarantor) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditGuarantorCreate
          credit={credit}
        />
      </Edit>
    ) : <Loader />;
  }

  if (showGuarantor) {
    const guarantor = credit.guarantors.find(g => g.id === +guarantorId);
    if (!guarantor) return null;

    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditGuarantor
          credit={credit}
          doctypes={guarantorDoctypes}
          guarantor={guarantor}
          isSuperadmin={isSuperadmin}
          editable={editable}
        />
      </Edit>
    ) : <Loader />;
  }

  if (showContracts) {
    return credit ? (
      <Edit {...props} title={<Title />} {...editProps}>
        <CreditContractsDetails credit={credit} setCredit={setCredit} isSuperadmin={isSuperadmin} />
      </Edit>
    ) : <Loader />;
  }

  const EditActions = () => {
    return editable ? (
      <Toolbar className={cls.toolbar}>
        <DeleteWithReasonButton credit={credit} />
        <ReminderComponent user={credit.user} credit={credit}/>
        <SaveButton label="Save" type="button" size="small" />
      </Toolbar>
    ) : null;
  };

  const isCompany = credit?.accredited === 'company';
  const backTo = `/credit/${credit.id}?initial`;

  return (
    <InitialPage title="Individual credit" backTo={backTo} backText="Back to credit">
      <Edit {...props} title={<Title />} transform={transform} {...editProps}>
        <SimpleForm toolbar={<EditActions />}>
          <Row gutter={[20, 20]} className={cls.columns}>
            <Col sm={24} md={11}>
              <CreditUser credit={credit} />
              <CreditInfo credit={credit} editable={editable} />
              <CreditCrops credit={credit} editable={editable} />
              <CreditDates credit={credit} editable={editable} />
              <CreditActions credit={credit} editable={editable} />
            </Col>
            <Col sm={24} md={13}>
              <CreditAccredited credit={credit} editable={editable} />
              <CreditSurveyProgress credit={credit}>
                <CreditSurveyView credit={credit} hide />
              </CreditSurveyProgress>
              <CreditFarms credit={credit} />
              <CreditIncomeVerification credit={credit} />
              <CreditDocumentsLink credit={credit} />
              <CreditComments credit={credit} editable={editable} onUpdated={reloadCredit} />
              <CreditHistory credit={credit} />
              <CreditFileScreeningWidget credit={credit} />
            </Col>
          </Row>
          <Row gutter={[20, 20]} className={cls.columns}>
            <Col sm={24}>
              <CreditFileStatusWidget credit={credit} />
            </Col>
          </Row>
        </SimpleForm>
      </Edit>

      <div className={cls.block} id="docs" ref={scrollRef}>
        <CreditDocuments
          key={credit?.accredited}
          credit={credit}
          isSuperadmin={isSuperadmin}
          editable={editable}
          isCompany={isCompany || false}
        />
      </div>

      <CreditGuarantors credit={credit} editable={editable} />
    </InitialPage>
  );
};

const useStyles = makeStyles({
  columns: {
    width: '100%',
  },
  block: {
    marginTop: 18,
  },
  toolbar: {
    position: 'fixed',
    left: 250,
    bottom: 0,
    zIndex: 1000,
    width: 'calc(100vw - 250px) !important',
    backgroundColor: 'var(--foreground-color) !important',
    padding: '0 66px !important',
    paddingRight: 'calc(66px + env(scrollbar-width, 0px))',
    borderTop: '1px solid var(--background-color)',
    display: 'flex !important',
    alignItems: 'center !important',
    transform: 'translateZ(0)',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    color: '#d32f2f !important',
    border: '1px solid #d32f2f',
    padding: '6px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    marginLeft: '0!important',
  },
});
